import { useEffect, useRef } from 'react';
import { getScreenInfo, trackScreenResize } from '../helpers/analytics';

/**
 * Custom hook to track screen size changes
 * This automatically tracks when users resize their browser window or rotate their device
 * Helps identify UI issues related to different screen sizes and device types
 * 
 * Usage:
 * - Add this hook to your main App component or layout component
 * - It will automatically track meaningful screen changes (device type or orientation)
 * - Events are throttled to prevent excessive tracking during window resize
 */
export const useScreenTracking = () => {
  // Store the previous screen info to detect meaningful changes
  const previousScreenInfoRef = useRef<any>(null);
  // Store timeout reference for proper cleanup
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Get initial screen info
    previousScreenInfoRef.current = getScreenInfo();

    const handleResize = () => {
      // Clear previous timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set new timeout to track resize after user stops resizing for 500ms
      debounceTimeoutRef.current = setTimeout(() => {
        const currentScreenInfo = getScreenInfo();

        // Track resize event (function will check if change is meaningful)
        trackScreenResize(previousScreenInfoRef.current);

        // Update previous screen info for next comparison
        previousScreenInfoRef.current = currentScreenInfo;
      }, 500);
    };

    const handleOrientationChange = () => {
      // Wait a bit for the orientation change to complete
      setTimeout(() => {
        const currentScreenInfo = getScreenInfo();
        trackScreenResize(previousScreenInfoRef.current);
        previousScreenInfoRef.current = currentScreenInfo;
      }, 150);
    };

    // Add event listener for window resize
    window.addEventListener('resize', handleResize, { passive: true });
    // Also track orientation changes on mobile devices
    window.addEventListener('orientationchange', handleOrientationChange, {
      passive: true,
    });

    // Cleanup event listeners on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Return screen info for components that might need it
  return previousScreenInfoRef.current;
};

